import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
    // Will be used by this service for making API calls
    axiosIns = null;

    // jwtConfig <= Will be used by this service
    jwtConfig = { ...jwtDefaultConfig };

    // For Refreshing Token
    isAlreadyFetchingAccessToken = false;

    // For Refreshing Token
    subscribers = [];

    constructor(axiosIns, jwtOverrideConfig) {
      this.axiosIns = axiosIns
      this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

      // Request Interceptor
      this.axiosIns.interceptors.request.use(
        config => config,
        error => Promise.reject(error),
      )

      // Add request/response interceptor
      this.axiosIns.interceptors.response.use(
        response => response,
        error => {
          const { config, response } = error || {}
          const status = response && response.status
          const isCsrfBootstrap = config && config.__isCsrfBootstrap

          const shouldHandle401 = status === 401
          // Only handle 403 after the CSRF retry has already been attempted by axios instance
          const shouldHandle403AfterRetry = status === 403 && config && config._csrfRetried === true && !isCsrfBootstrap

          if (shouldHandle401 || shouldHandle403AfterRetry) {
            // Determine message based on current language
            const langIsSv = localStorage.getItem('lang') === 'sv'
            const msgSource = (response && response.data) || {}
            const message = (langIsSv ? msgSource.message : msgSource.eng_message)
                        || msgSource.detail
                        || 'Session expired'

            // Clear any persisted auth state
            try {
              localStorage.removeItem('userData')
              localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
              localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
            } catch (e) {
              // ignore storage errors
            }

            // Redirect to login if not already there
            try {
              const isOnAuthPage = ['/login', '/signup', '/forgot-password', '/reset-password'].includes(window.location.pathname)
              if (!isOnAuthPage) {
                // Use replace to avoid navigating back to a protected stale page
                window.location.replace('/login?q=tokenExpired')
              }
            } catch (e) {
              // ignore window errors (e.g., SSR)
            }

            // Keep returning a resolved object to avoid breaking existing callers
            return { status, message }
          }
          // Propagate other errors (including initial 403 so the axios instance can retry CSRF once)
          return Promise.reject(error)
        },
      )
    }

    onAccessTokenFetched(accessToken) {
      this.subscribers = this.subscribers.filter(callback => callback(accessToken))
    }

    addSubscriber(callback) {
      this.subscribers.push(callback)
    }

    getToken() {
      return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
    }

    getRefreshToken() {
      return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
    }

    setToken(value) {
      localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    }

    setRefreshToken(value) {
      localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
    }

    // Ensure CSRF cookie exists before login so axios attaches X-CSRFToken
    async ensureCsrf() {
      try {
        await this.axiosIns.get('auth/csrf', {
          // Make sure no legacy auth header is sent
          transformRequest: (data, headers) => {
            if (headers && headers.Authorization) delete headers.Authorization
            return data
          },
        })
      } catch (e) {
        // Non-blocking: backend may already have set a valid CSRF cookie
      }
    }

    async login(...args) {
      await this.ensureCsrf()
      return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
    }

    async requestResetPassword(...args) {
      // Ensure we operate with the freshest CSRF token before unsafe POST
      await this.ensureCsrf()
      return this.axiosIns.post(this.jwtConfig.requestResetPassword, ...args)
    }

    async resetUpdatePassword(...args) {
      // Ensure we operate with the freshest CSRF token before unsafe POST
      await this.ensureCsrf()
      return this.axiosIns.post(this.jwtConfig.resetPassword, ...args)
    }

    register(...args) {
      return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
    }

    packages(...args) {
      return this.axiosIns.get(this.jwtConfig.packages, ...args)
    }

    subscription(...args) {
      return this.axiosIns.get(this.jwtConfig.subscriptions, ...args)
    }

    updateSubscription(uuid, ...args) {
      return this.axiosIns.put(
        `${this.jwtConfig.subscriptions}${uuid}`,
        ...args,
      )
    }

    cancelSubscription(uuid, ...args) {
      return this.axiosIns.put(
        `${this.jwtConfig.subscriptions}${uuid}/cancel`,
        ...args,
      )
    }

    changePaymentPlan(args) {
      const body = args && args.data ? args.data : args.payment_plan
      return this.axiosIns.put(
        `${this.jwtConfig.subscriptions}${args.id}/change_plan`,
        body,
      )
    }

    stats(...args) {
      return this.axiosIns.get(this.jwtConfig.stats, ...args)
    }

    billings(args) {
      return args
        ? this.axiosIns.get(this.jwtConfig.billing, args)
        : this.axiosIns.get(this.jwtConfig.billing)
    }

    billingsDetail() {
      return this.axiosIns.get(this.jwtConfig.billingDetail)
    }

    updateBillingsDetail(...args) {
      return this.axiosIns.put(this.jwtConfig.billingDetail, ...args)
    }

    configure(...args) {
      return this.axiosIns.get(this.jwtConfig.configure, ...args)
    }

    updateConfigure(uuid, ...args) {
      return this.axiosIns.put(
        `${this.jwtConfig.configure}/${uuid}`,
        ...args,
      )
    }

    getBookKeepList() {
      return this.axiosIns.get(this.jwtConfig.bookKeep)
    }

    addBookKeepList(...args) {
      return this.axiosIns.post(this.jwtConfig.bookKeep, ...args)
    }

    updateBookKeepList(uuid, ...args) {
      return this.axiosIns.put(`${this.jwtConfig.bookKeep}/${uuid}`, ...args)
    }

    deleteBookKeepList(uuid) {
      return this.axiosIns.delete(`${this.jwtConfig.bookKeep}/${uuid}`)
    }

    getRegularCriteriaList() {
      return this.axiosIns.get(this.jwtConfig.regularCriteria)
    }

    addRegularCriteriaList(...args) {
      return this.axiosIns.post(this.jwtConfig.regularCriteria, ...args)
    }

    updateRegularCriteriaList(uuid, ...args) {
      return this.axiosIns.put(
        `${this.jwtConfig.regularCriteria}/${uuid}`,
        ...args,
      )
    }

    deleteRegularCriteriaList(uuid) {
      return this.axiosIns.delete(
        `${this.jwtConfig.regularCriteria}/${uuid}`,
      )
    }

    getNoxCriteriaList() {
      return this.axiosIns.get(this.jwtConfig.noxCriteria)
    }

    addNoxCriteriaList(...args) {
      return this.axiosIns.post(this.jwtConfig.noxCriteria, ...args)
    }

    updateNoxCriteriaList(uuid, ...args) {
      return this.axiosIns.put(
        `${this.jwtConfig.noxCriteria}/${uuid}`,
        ...args,
      )
    }

    deleteNoxCriteriaList(uuid) {
      return this.axiosIns.delete(`${this.jwtConfig.noxCriteria}/${uuid}`)
    }

    listHelpText(...args) {
      return this.axiosIns.get(this.jwtConfig.helpText, ...args)
    }

    billingDownload(id) {
      return this.axiosIns.get(`${this.jwtConfig.billing}${id}/download`)
    }

    billingDownloadByToken(token) {
      return this.axiosIns.get(`billings-download/${token}`)
    }

    faqCategories(...args) {
      return this.axiosIns.get(this.jwtConfig.faqCategories, ...args)
    }

    faqList(...args) {
      if (args) {
        return this.axiosIns.get(
          `${this.jwtConfig.listfaqByCategories}/${args}`,
        )
      } return this.axiosIns.get(
        this.jwtConfig.listfaqByCategories,
        ...args,
      )
    }

    eventsList(...args) {
      return this.axiosIns.get(this.jwtConfig.failure, ...args)
    }

	    triggerSync(...args) {
	      return this.axiosIns.post(this.jwtConfig.triggerSync, ...args)
	    }


    tierList() {
      return this.axiosIns.get(this.jwtConfig.tierList)
    }

    changeTier(...args) {
      return this.axiosIns.post(this.jwtConfig.changeTier, ...args)
    }

    blogsList(...args) {
      if (args) {
        return this.axiosIns.get(this.jwtConfig.listBlog, ...args)
      } return this.axiosIns.get(this.jwtConfig.listBlog)
    }

    blogDetails(uuid, ...args) {
      return this.axiosIns.get(`${this.jwtConfig.listBlog}/${uuid}`, ...args)
    }

    blogCategories() {
      return this.axiosIns.get(this.jwtConfig.blogCategories)
    }

    // Subscription health check for accounting connection
    subscriptionHealth(...args) {
      return this.axiosIns.get(this.jwtConfig.subscriptionHealth, ...args)
    }

    // Fortnox connect - returns auth_link to redirect
    connectFortnox(params) {
      if (params) {
        return this.axiosIns.get(this.jwtConfig.connectFortnox, { params })
      }
      return this.axiosIns.get(this.jwtConfig.connectFortnox)
    }

    // Spiris connect - returns auth_link to redirect
    connectSpiris(params) {
      if (params) {
        return this.axiosIns.get(this.jwtConfig.connectSpiris, { params })
      }
      return this.axiosIns.get(this.jwtConfig.connectSpiris)
    }

    // Generic accounting connect helper
    connectAccounting(program = 'fortnox', params) {
      const normalized = String(program || '').toLowerCase()
      if (normalized === 'spiris') {
        return this.connectSpiris(params)
      }
      return this.connectFortnox(params)
    }

    refreshToken() {
      return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
        refreshToken: this.getRefreshToken(),
      })
    }
}
